import { fromJS, List } from 'immutable';
import * as workspacesActionTypes from '../actions/workspacesActionTypes';
import { getKeyPath } from '../utils/immutableUtils';
import * as actionTypes from "../actions/thesaurusActionTypes";

const initialState = fromJS({
  workspaceList: [],
  isRetrievingRoot: false,
  rootRequest: null,
  retrieveChildrenRequests: {},
  deleteRequests: {},
  editRequests: {},
  createRequests: [],
});

const createWorkspaceData = (id, name, description) => (fromJS({
  n: name,
  d: description,
  t: 'L',
  i: id,
  x: '0',
  opk: '0',
  ch: 'FALSE',
  u: '',
  ct: [],
  users: [],
  xpnd: false, // isExpanded- used to remember your category tree location
}));

const workspaces = (state = initialState, action) => {
  switch (action.type) {
    case workspacesActionTypes.WORKSPACES_RETRIEVE_REQUEST:
      if (action.workspaceID) {
        // request is for a child category, not root
        if (state.getIn(['retrieveChildrenRequests', action.workspaceID]) &&
          !(state.getIn(['retrieveChildrenRequests', action.workspaceID]) instanceof Error)) {
          // request already exists for this so cancel the current one
          state.getIn(['retrieveChildrenRequests', action.workspaceID]).cancel();
        }

        return state
          .mergeDeep(fromJS({ retrieveChildrenRequests: { [action.workspaceID]: action.requestPromise } }))
          ;
      } else if (state.get('rootRequest')) {
        // cancel the current root request promise since it exists
        state.get('rootRequest').cancel();
      }

      return state
        .set('isRetrievingRoot', true)
        .set('rootRequest', action.requestPromise)
        ;
    case workspacesActionTypes.WORKSPACES_RETRIEVE_SUCCESS:
      if (action.workspaceID) {
        // request is for a child category, not root

        let newCategoryList;
        const retrieveChildrenRequests = state.get('retrieveChildrenRequests').delete(action.workspaceID);
        const foundPath = getKeyPath(state.get('workspaceList'), 'i', action.workspaceID, 'ct');

        if (foundPath) {
          newCategoryList = state
            .get('workspaceList')
            .setIn([...foundPath, 'ct'], fromJS(action.workspaceList))
            .setIn([...foundPath, 'xpnd'], true);
        }

        return state
          .set('workspaceList', newCategoryList || state.get('workspaceList'))
          .set('retrieveChildrenRequests', retrieveChildrenRequests)
          ;
      }

      return state
        .set('isRetrievingRoot', false)
        .set('rootRequest', null)
        .set('workspaceList', fromJS(action.workspaceList))
        .set('retrieveChildrenRequests', fromJS({}))
        ;
    case workspacesActionTypes.WORKSPACES_RETRIEVE_FAILURE:
      if (action.workspaceID) {
        return state
          .setIn(['retrieveChildrenRequests', action.workspaceID], action.err)
          ;
      }

      return state
        .set('isRetrievingRoot', false)
        .set('rootRequest', action.err)
        ;
    case workspacesActionTypes.WORKSPACES_CREATE_REQUEST: {
      const foundRequestIndex = state.get('createRequests').findKey(createRequest =>
        createRequest.get('workspaceName') === action.workspaceName &&
        createRequest.get('workspaceParentID') === action.workspaceParentID
      );

      if (foundRequestIndex !== undefined) {
        // request with same details already exist, so just overwrite that one
        return state
          .setIn(['createRequests', foundRequestIndex, 'request'], action.requestPromise)
          .setIn(['createRequests', foundRequestIndex, 'workspaceDescription'],
            action.workspaceDescription)
          ;
      }

      return state.set('createRequests', state.get('createRequests').push(fromJS({
        workspaceName: action.workspaceName,
        workspaceParentID: action.workspaceParentID,
        workspaceDescription: action.workspaceDescription,
        request: action.requestPromise,
      })));
    }
    case workspacesActionTypes.WORKSPACES_CREATE_SUCCESS: {
      const foundRequestIndex = state.get('createRequests').findKey(createRequest =>
        createRequest.get('workspaceName') === action.workspaceName &&
          createRequest.get('workspaceParentID') === action.workspaceParentID
      );

      if (foundRequestIndex !== undefined) {
        const newState = state.deleteIn(['createRequests', foundRequestIndex]);

        if (action.workspaceParentID && action.workspaceParentID !== '0') {
          const foundKeyPath = getKeyPath(state.get('workspaceList'), 'i', action.workspaceParentID, 'ct');

          // adds new workspace to a non-root level workspace
          if (foundKeyPath) {
            let newChildList = newState.getIn(['workspaceList', ...foundKeyPath, 'ct']);

            if (newChildList) {
              newChildList = newChildList.push(createWorkspaceData(
                action.workspaceID,
                action.workspaceName,
                action.workspaceDescription
              ));
            } else {
              newChildList = new List([createWorkspaceData(
                action.workspaceID,
                action.workspaceName,
                action.workspaceDescription
              )]);
            }

            return newState
              .setIn(['workspaceList', ...foundKeyPath, 'ch'], 'TRUE')
              .setIn(['workspaceList', ...foundKeyPath, 'ct'], newChildList);
          }

          return newState;
        }

        // adds new workspace to root level workspace
        return newState.set('workspaceList', state.get('workspaceList')
            .push(createWorkspaceData(
              action.workspaceID,
              action.workspaceName,
              action.workspaceDescription
            )));
      }

      return state;
    }
    case workspacesActionTypes.WORKSPACES_CREATE_FAILURE: {
      const foundRequestIndex = state.get('createRequests').findKey(createRequest =>
        createRequest.get('workspaceName') === action.workspaceName &&
        createRequest.get('workspaceParentID') === action.workspaceParentID
      );

      if (foundRequestIndex !== undefined) {
        return state.setIn(['createRequests', foundRequestIndex, 'request'], action.err);
      }

      return state;
    }
    case workspacesActionTypes.WORKSPACES_EDIT_REQUEST:
      return state.setIn(['editRequests', action.workspaceID], fromJS({
        workspaceName: action.workspaceName,
        workspaceDescription: action.workspaceDescription,
        request: action.requestPromise,
      }));
    case workspacesActionTypes.WORKSPACES_EDIT_SUCCESS:
      if (state.getIn(['editRequests', action.workspaceID])) {
        let newState = state.deleteIn(['editRequests', action.workspaceID]);

        if (action.workspaceName || action.workspaceDescription) {
          const foundPath = getKeyPath(state.get('workspaceList'), 'i', action.workspaceID, 'ct');
          if (foundPath) {
            if (action.workspaceName) {
              newState = newState.setIn(['workspaceList', ...foundPath, 'n'], action.workspaceName);
            }
            if (action.workspaceDescription) {
              newState = newState.setIn(['workspaceList', ...foundPath, 'd'], action.workspaceDescription);
            }
          }
        }

        return newState;
      }
      return state;
    case workspacesActionTypes.WORKSPACES_EDIT_FAILURE:
      return state.setIn(['editRequests', action.workspaceID, 'request'], action.err);
    case workspacesActionTypes.WORKSPACES_DELETE_REQUEST:
    case workspacesActionTypes.WORKSPACES_UNSUBSCRIBE_REQUEST:
    {
      const foundPath = getKeyPath(state.get('workspaceList'), 'i', action.workspaceID, 'ct');
      if (foundPath) {
        const workspaceData = state.getIn(['workspaceList', ...foundPath]);
        return state.setIn(['deleteRequests', action.workspaceID], fromJS({
          workspaceName: workspaceData.get('n'),
          workspaceDescription: workspaceData.get('d'),
          request: action.requestPromise,
        }));
      }

      return state;
    }
    case workspacesActionTypes.WORKSPACES_DELETE_SUCCESS:
    case workspacesActionTypes.WORKSPACES_UNSUBSCRIBE_SUCCESS:
    {
      const foundPath = getKeyPath(state.get('workspaceList'), 'i', action.workspaceID, 'ct');
      return state
        .deleteIn(['deleteRequests', action.workspaceID])
        .deleteIn(['workspaceList', ...foundPath])
        ;
    }
    case workspacesActionTypes.WORKSPACES_DELETE_FAILURE:
    case workspacesActionTypes.WORKSPACES_UNSUBSCRIBE_FAILURE:
      return state.setIn(['deleteRequests', action.workspaceID, 'request'], action.err);
    case workspacesActionTypes.WORKSPACE_COLLAPSE:
      return state.setIn(['deleteRequests', action.categoryID, 'request'], action.err);
    case workspacesActionTypes.WORKSPACE_LIST_CLEAR: {
      let newCategoryList = state.get('workspaceList');
      const foundPath = getKeyPath(state.get('workspaceList'), 'i', action.categoryID, 'ct');

      if (foundPath) {
        newCategoryList = state
          .get('workspaceList')
          .setIn([...foundPath, 'ct'], new List())
          .setIn([...foundPath, 'xpnd'], false);
      }
      return state
        .set('workspaceList', newCategoryList)
        ;
    }
    case workspacesActionTypes.WORKSPACES_CANCEL_ALL_REQUESTS:
      state.get('retrieveChildrenRequests').find((value) => {
        if (value.cancel) {
          value.cancel();
        }
        return false;
      });

      if (state.get('rootRequest') && state.get('rootRequest').cancel) {
        state.get('rootRequest').cancel();
      }

      return state
        .set('isRetrievingRoot', false)
        .set('rootRequest', null)
        .set('retrieveChildrenRequests', fromJS({}))
        ;
    case workspacesActionTypes.WORKSPACES_CANCEL_CREATE_REQUEST: {
      const foundRequestIndex = state.get('createRequests').findKey(createRequest =>
        createRequest.get('workspaceName') === action.workspaceName &&
        createRequest.get('workspaceParentID') === action.workspaceParentID
      );

      if (foundRequestIndex !== undefined) {
        // first, cancel the promise
        const request = state.getIn(['createRequests', foundRequestIndex, 'request']);

        if (request && request.cancel) {
          request.cancel();
        }

        return state.deleteIn(['createRequests', foundRequestIndex]);
      }

      return state;
    }
    case workspacesActionTypes.WORKSPACES_RETRIEVE_INFO_REQUEST:
      return state;
    case workspacesActionTypes.WORKSPACES_RETRIEVE_INFO_SUCCESS:
      const foundPath = getKeyPath(state.get('workspaceList'), 'i', action.workspaceID, 'ct');

      let newCategoryList;
      if (foundPath) {
        newCategoryList = state
          .get('workspaceList')
          .setIn([...foundPath, 'users'], fromJS(action.workspaceUsers))
          .setIn([...foundPath, 'isOwner'], fromJS(action.isOwner))
          .setIn([...foundPath, 'shareExpDate'], fromJS(action.shareExpDate));
      }

      return state
        .set('workspaceList', newCategoryList || state.get('workspaceList'));
    case workspacesActionTypes.WORKSPACES_RETRIEVE_INFO_FAILURE:
      return state;
    default:
      return state;
  }
};

export default workspaces;
